import { useLayoutEffect } from "react";
import { z } from "zod";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { RoomJoinedModal } from "../../components/modals/room/RoomJoinedModal";
import { FeedEndAskGpt } from "../../components/gpt/FeedEndAskGpt";
import { IconArrowDown, IconLayoutGrid, IconSearch } from "@tabler/icons-react";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { DesktopPracticeFeedPage } from "./desktop/DesktopPracticeFeedPage";
import { NavLang } from "../../components/app/headers/layout";
import ProfilePicture from "../../components/ProfilePicture";
import Navbar from "../../components/app/navbars/Navbar";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { registerDiscoveryScrollContainer } from "../../domains/app/stores/discoveryScrollStore";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import { PromoCard } from "../../components/ui/PromoCard";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { Avatar } from "../../components/ui/Avatar";
import { PracticeFeedRail } from "../../components/practice-feed/PracticeFeedRail";
import { PracticeDropdown } from "../../components/practice-feed/PracticeDropdown";
import { DiscoveryCongressHero } from "../../components/discovery/DiscoveryCongressHero";
import { type PracticeRailCorpus } from "../../domains/practice/hooks/usePracticeRailTerms";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { usePullToRefresh } from "../../domains/forYou/hooks/usePullToRefresh";
import Loader from "../../components/Loader";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import {
  getRoomName,
  getRoomDescription,
} from "../../domains/room/hooks/useRoomName";
import {
  FeedItem,
  PracticeArticleFeedItem,
  PracticeCardSkeleton,
  PracticeFeedEmpty,
  PracticeFeedFailed,
  PracticeRailViewed,
  RoomCollectionsSection,
  RoomRecommendationImpression,
  isItemNew,
  railTermForArticle,
} from "./PracticeFeedItems";
import { usePracticeFeedPage } from "./usePracticeFeedPage";

/**
 * The page's own header, because the practice DROPDOWN stands where the title
 * used to. `Header` takes a `string` title, and widening it to a `ReactNode` would
 * change the chrome of every other surface for one page's benefit — so this page
 * owns the bar and keeps its measurements (`px-5 py-3`, the `min-h-12` that
 * matches every other header's two-line height, the same blur and border).
 *
 * There is no "Discovery" title left: the corpus name IS the title now, and it is
 * the ONLY thing on this side of the bar. The greeting went with the title it sat
 * under. A practice name runs 40 characters in the median, so a second line under
 * it made the header say a lot and mean little, and the greeting is the half that
 * told the reader nothing they could act on.
 */
function PracticeFeedHeader({
  practices,
  corpus,
  onSelectCorpus,
}: {
  practices: ReadonlyArray<{ uid: string; name: string }>;
  corpus: PracticeRailCorpus | null;
  onSelectCorpus: (corpus: PracticeRailCorpus) => void;
}): React.JSX.Element {
  const history = useHistory();
  const { currentUser } = useCurrentUser();

  return (
    <div className="border-b border-secondary-soft-yellow-grey bg-white/90 px-5 py-3 backdrop-blur-md">
      {/* `min-h-12` stays although the second line is gone: it reserves the
          two-line height the shared `Header` reserves, so both arms of the
          comparison have a header of the same height. */}
      <div className="flex min-h-12 items-center justify-between gap-3">
        <PracticeDropdown
          practices={practices}
          corpus={corpus}
          onSelectCorpus={onSelectCorpus}
        />
        <div className="flex shrink-0 items-center gap-4">
          <IconSearch
            className="size-6 cursor-pointer text-primary-black"
            onClick={() => {
              history.push("/search");
            }}
          />
          <NavLang />
          {currentUser !== null && (
            <Link to="/profile">
              <ProfilePicture user={currentUser} height={32} badge={null} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// Router state carried from post-onboarding room-invite auto-join: the invited
// room's display data + its in-app path, so the RoomJoinedModal needs no
// refetch. Absent on every normal Discovery visit (field default null).
const practiceFeedLocationStateSchema = z.object({
  joinedRoom: z
    .object({
      roomName: z.string(),
      organisationName: z.string(),
      logoUrl: z.string(),
      roomPath: z.string(),
    })
    .nullable()
    .default(null),
});

function PracticeFeedRoomJoinedModal(): React.JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { joinedRoom } = useLocationState(practiceFeedLocationStateSchema);

  return (
    <RoomJoinedModal
      room={
        joinedRoom === null
          ? null
          : {
              roomName: joinedRoom.roomName,
              organisationName: joinedRoom.organisationName,
              logoUrl: joinedRoom.logoUrl,
            }
      }
      // Drop the router state so the modal animates out and never re-appears on
      // a back-navigation to Discovery.
      onClose={() => {
        history.replace(location.pathname + location.search);
      }}
      onDiscover={() => {
        if (joinedRoom !== null) history.push(joinedRoom.roomPath);
      }}
    />
  );
}

/**
 * The practice arm of `/discovery`: the same surface, fed by the reader's
 * practice instead of their topics.
 *
 * A DUPLICATE of {@link DiscoveryPage} rather than a branch inside it. The two
 * differ in what the chips are, what the feed reads and what the empty state
 * says — a flag threaded through one component would have to be consulted at
 * each of those points, and the arm that loses would then be removed by
 * unpicking a dozen conditionals instead of deleting a file.
 *
 * Desktop deliberately still renders the control page: this arm is mobile-first
 * (the whole cohort it is measured on is), and serving desktop a half-migrated
 * surface would put topic chips above a practice feed.
 */
export default function PracticeFeedPage(): React.JSX.Element {
  return (
    <>
      {isDesktopLayout() ? (
        <DesktopPracticeFeedPage />
      ) : (
        <MobilePracticeFeedPage />
      )}
      <PracticeFeedRoomJoinedModal />
    </>
  );
}

function MobilePracticeFeedPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const {
    scrollRef,
    rememberScroll,
    view,
    selection,
    selectedRoomId,
    selectedDescriptorUi,
    filters,
    selectedPractice,
    selectedPracticeName,
    practices,
    rooms,
    hasRooms,
    roomFeed,
    refresh,
    railTerms,
    effectiveDescriptorUi,
    litTerm,
    practiceFeed,
    practiceArticles,
    practiceDescriptorUis,
    topRecommendedRoom,
    feedArticles,
    WEBAPP_PRACTICE_CARD_MESH_CHIP,
    handleSelectChip,
    handleChangeFilters,
    handleClearAll,
    handleSelectCorpus,
    handleAddRoom,
    handleDiscoverRoom,
  } = usePracticeFeedPage();

  // Pull-to-refresh mints a new ranking seed, which only the ranked feed has:
  // the practice surfaces are a Boolean's matched set ordered by recency, so
  // there is nothing to rotate and a spinner would promise a change that cannot
  // happen. Room mode is a recency archive the seed doesn't touch either, and
  // offline reads from the public cache.
  const {
    contentRef,
    indicatorRef,
    status: pullStatus,
  } = usePullToRefresh({
    scrollRef,
    enabled: false,
    isRefreshing: roomFeed.isFetching,
    onRefresh: refresh,
  });

  useLayoutEffect(() => {
    registerDiscoveryScrollContainer(scrollRef.current);
    return () => {
      registerDiscoveryScrollContainer(null);
    };
  }, []);

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      background="var(--color-ds-white)"
      onScroll={rememberScroll}
      header={
        <PracticeFeedHeader
          practices={practices}
          corpus={view === null ? null : view.corpus}
          onSelectCorpus={handleSelectCorpus}
        />
      }
      footer={<Navbar />}
      onScrollEnd={() => {
        if (selectedRoomId !== null) {
          if (roomFeed.canFetchNextPage) roomFeed.fetchNextPage();
          return;
        }
        if (practiceFeed.state === "ready" && practiceFeed.canFetchNextPage)
          practiceFeed.fetchNextPage();
      }}
    >
      <div className="relative flex min-h-full flex-col bg-white">
        {/* Pull-to-refresh band. Height (and the content's translateY) is driven
            imperatively by usePullToRefresh — no per-frame re-render, no reflow;
            the content is pushed DOWN so the band gets its own space. Default
            collapsed via `h-0` (NOT an inline style, which a re-render would
            reset). Shows the arrow + hint while dragging, the spinner only while
            the feed is actually fetching. */}
        <div
          ref={indicatorRef}
          className="pointer-events-none absolute inset-x-0 top-0 z-10 flex h-0 items-center justify-center overflow-hidden"
        >
          {pullStatus === "refreshing" ? (
            <Loader />
          ) : pullStatus === "pulling" ? (
            <div className="flex items-center gap-2 text-ds-slate-500">
              <IconArrowDown size={18} stroke={1.5} />
              <span className="font-dm-sans text-footnote font-medium">
                {t("Pull to refresh")}
              </span>
            </div>
          ) : null}
        </div>

        <div ref={contentRef} className="flex flex-1 flex-col gap-6 pb-6">
          {/* The rail's own denominator, counted only where a rail is actually
              on screen. `ready` and NOT "not in Room mode": `loading` is a
              skeleton, and `unsupported` is the Boolean failing to compile —
              the chips leave and the feed says so. Counting either would put
              rail views under a rail nobody was shown, which is the same
              denominator inflation this pair exists to remove.

              A `ready` rail carrying no term still counts: an over-represented
              descriptor is what a thin corpus lacks, and a thin corpus is a
              fact about the literature, not a failure. */}
          {view !== null && railTerms.status === "ready" && (
            <PracticeRailViewed corpus={view.corpus} />
          )}

          <PracticeFeedRail
            layout="mobile"
            rail={railTerms}
            rooms={rooms}
            selection={selection}
            onSelect={handleSelectChip}
            filters={filters}
            onChangeFilters={handleChangeFilters}
            onClearAll={handleClearAll}
            // Which corpus the drawer describes. `null` ONLY in Room mode,
            // which is also what takes the pinned chip off the row: a Room has
            // none of these axes and no MeSH chip to clear.
            //
            // The aggregate passes through as itself. It has no axis values —
            // nothing counts journals or publication types over a union — but it
            // does have a live chip, and the pinned chip is the exit that never
            // scrolls. Withholding it there would drop that guarantee on the one
            // rail merged from up to six practices.
            drawerCorpus={
              view === null || selectedRoomId !== null ? null : view.corpus
            }
            practiceLabel={
              selectedPracticeName === null ? "" : selectedPracticeName
            }
          />

          {/* The congress hero belongs to the whole-practice surface only, same
              as it belonged to For You. The interests deep-link is gone with the
              topics it pointed at — Settings → My practice is this arm's way in,
              and the feed's empty state links straight to it. */}
          {/* The congress hero belongs to the practice surface, not to a Room:
              a Room's own content is what a reader taps a Room for. It used to
              be pinned to the aggregate chip, which was the landing tab; with
              one practice per chip, pinning it to the FIRST practice would make
              it appear and vanish depending on which of their own practices
              someone is reading. */}
          {selectedRoomId === null && <DiscoveryCongressHero enabled />}

          {!hasRooms && (
            <div className="px-5">
              <PromoCard
                leading={
                  <Avatar
                    variant="icon"
                    size="md"
                    icon={<IconLayoutGrid size={16} stroke={1.5} />}
                    badge={null}
                  />
                }
                title={t("Add your first room")}
                description={t(
                  "Rooms deliver curated scientific content tailored to your specialty handpicked from leading sources."
                )}
                onClick={handleAddRoom}
              />
            </div>
          )}

          {/* Only in room mode: see `RoomCollectionsSection` for why the
              personalised rail is not here. */}
          {selectedRoomId !== null && (
            <RoomCollectionsSection roomId={selectedRoomId} layout="mobile" />
          )}

          <div className="flex flex-col gap-3 px-5">
            {/* A Room keeps its visible title: it separates this feed from the
                collections rail right above it. A practice gets none — the header
                dropdown carries its name, and saying it twice was the weight the
                design asked us to drop. It keeps a HEADING though: the dropdown is
                a button, so without this the whole feed has no entry in the
                heading tree to navigate to. */}
            {selectedRoomId !== null ? (
              <SectionTitle title={t("Room publications")} />
            ) : (
              selectedPracticeName !== null && (
                <h2 className="sr-only">{selectedPracticeName}</h2>
              )
            )}

            <div className="flex flex-col divide-y divide-ds-slate-100 *:py-4 [&>*:first-child]:pt-0">
              {selectedRoomId !== null ? (
                roomFeed.isLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <SkeletonPublicationCard
                      showImage
                      key={idx}
                      showDescription
                    />
                  ))
                ) : feedArticles.length === 0 ? (
                  <p className="py-6 text-center text-footnote text-ds-slate-500">
                    {t("No content to show right now.")}
                  </p>
                ) : (
                  feedArticles.map((item) => (
                    <FeedItem
                      key={item.id}
                      layout={{ kind: "card" }}
                      item={item}
                      roomId={selectedRoomId}
                      surface="room"
                      isNew={isItemNew(item)}
                    />
                  ))
                )
              ) : view === null || practiceFeed.state === "pending" ? (
                // `view === null` is the catalogue not landed yet, which is a
                // load and not an empty corpus. It shares this branch rather
                // than reading the feed's own report, which for a query nobody
                // switched on is "ready, holding nothing".
                Array.from({ length: 5 }).map((_, idx) => (
                  <PracticeCardSkeleton
                    key={idx}
                    showChip={WEBAPP_PRACTICE_CARD_MESH_CHIP}
                  />
                ))
              ) : practiceFeed.state === "error" ? (
                // The evaluation failed, which is NOT an empty corpus — the
                // endpoint 501s on a practice whose Boolean it cannot evaluate,
                // on purpose. Rendering that as "we hold none yet" would blame
                // the literature for our own outage.
                <PracticeFeedFailed />
              ) : practiceArticles.length === 0 ? (
                // A practice with nothing behind it is a fact about the corpus,
                // not a failure: the Boolean is evaluated live, so this says
                // "we hold none yet" and never "there is none". A failed request
                // lands on the branch above instead.
                <PracticeFeedEmpty />
              ) : (
                practiceArticles.map((article) => (
                  <PracticeArticleFeedItem
                    key={article.id}
                    item={article}
                    corpus={view.corpus}
                    // The phone's swipe list is not the practice feed's.
                    openedFrom={null}
                    layout={{
                      kind: "card",
                      // Gated here and not inside the card: `chip === null` is
                      // already what an empty rail produces (a chip outside the
                      // rail could not be selected, so there is never a fallback
                      // to draw), so the flag off reuses a path that exists and
                      // is already right rather than adding a second one.
                      chip: WEBAPP_PRACTICE_CARD_MESH_CHIP
                        ? railTermForArticle(
                            railTerms.status === "ready" ? railTerms.terms : [],
                            practiceDescriptorUis,
                            article.id
                          )
                        : null,
                      selectedDescriptorUi,
                      onSelectDescriptor: (descriptorUi) => {
                        handleSelectChip({ kind: "mesh", descriptorUi });
                      },
                    }}
                  />
                ))
              )}
            </div>
            {/* One practice only: "All practices" has no single Boolean to
                hand the agent (`selectedPractice` is undefined there). Shown
                once the last page is in; `hasNextPage` rather than
                `canFetchNextPage` so a background refetch does not blink the
                card. After the divided list, not in it: the last article keeps
                no rule under it. A lit chip rides along as the reference's
                `descriptorUi` and as the card's subject; the drawer's journal
                and type axes do not, PubMed has no reading of them. */}
            {selectedPractice !== undefined &&
              practiceFeed.state === "ready" &&
              practiceArticles.length > 0 &&
              !practiceFeed.hasNextPage &&
              (effectiveDescriptorUi === null || litTerm !== null) && (
                <FeedEndAskGpt
                  feedScope={{
                    kind: "practice",
                    uid: selectedPractice.uid,
                    descriptorUi: effectiveDescriptorUi,
                  }}
                  title={selectedPractice.name}
                  narrowing={litTerm === null ? null : litTerm.label}
                />
              )}
          </div>

          {topRecommendedRoom !== null && (
            <div className="px-5">
              <RoomRecommendationImpression
                roomId={topRecommendedRoom.id}
                roomName={getRoomName(topRecommendedRoom, activeLang)}
              />
              <PromoCard
                leading={
                  <Avatar
                    variant="image"
                    size="md"
                    src={getRoomLogoUrl(topRecommendedRoom)}
                    alt={getRoomName(topRecommendedRoom, activeLang)}
                    imageFit="scale-down"
                    badge={null}
                  />
                }
                title={`${t("Discover")} ${getRoomName(topRecommendedRoom, activeLang)} ${t("Room")}`}
                description={
                  getRoomDescription(topRecommendedRoom, activeLang) ?? ""
                }
                onClick={() => {
                  handleDiscoverRoom(topRecommendedRoom.id);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </ScrollableLayout>
  );
}
